import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent, DialogFooter } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { ArrowLeft, FileText, Mail, MapPin, AlertCircle, CheckCircle, Loader2, Edit3, Download, FileSignature, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { useReportGeneration } from '../../../../../redux/WeldData/hooks/useWeldData';
import { Notification } from '../../../../../components/ui/custom-notification';
import { ReportColumnConfigModal } from './ReportColumnConfigModal';
import { getReportColumns } from '../../../../../services/reportColumnsApi';
import { SignatureDialog } from '../../../../../components/ui/SignatureDialog';
import { Signature } from './tabs/Signature';
import { useTeamMembers } from '../../../../../redux/ProductionData/hooks/useTeamMembers';
import { useDispatch, useSelector } from 'react-redux';
import { generateReportAssembly, clearGenerateReportAssembly, productionSheetSaveSignature, clearProductionSheetSaveSignature, fetchProductionData, clearProductionData } from '../../../../../redux/ProductionData/actions';

/** Saved API ids may be team_member_id or user_id; Select options use team_member_id as value. */
function resolveSavedIdToTeamMemberSelectValue(
  savedId: string | number | null | undefined,
  list: Array<{ value: string; userId?: string | number }>
): string | null {
  if (savedId == null || savedId === '') return null;
  const idStr = String(savedId);
  const byValue = list.find((m) => m.value === idStr);
  if (byValue) return byValue.value;
  const byUserId = list.find((m) => m.userId != null && String(m.userId) === idStr);
  return byUserId ? byUserId.value : null;
}

function normalizePersonName(name: string | undefined | null): string {
  if (!name) return '';
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** When saved id is missing from the filtered list, match saved signer name to list entry (label/name). */
function resolveSavedIdToTeamMemberSelectValueWithNameFallback(
  savedId: string | number | null | undefined,
  savedName: string | undefined | null,
  list: Array<{ value: string; label: string; name?: string; userId?: string | number }>
): string | null {
  const byId = resolveSavedIdToTeamMemberSelectValue(savedId, list);
  if (byId) return byId;
  const target = normalizePersonName(savedName);
  if (!target) return null;
  for (const m of list) {
    if (normalizePersonName(m.name) === target) return m.value;
    const labelName = m.label?.replace(/\s*\([^)]*\)\s*$/, '').trim() || '';
    if (normalizePersonName(labelName) === target) return m.value;
  }
  return null;
}

function hasMeaningfulSavedSignatureBlock(block: any): boolean {
  if (!block) return false;
  return Boolean(
    (block.signature && String(block.signature).trim()) ||
      (block.stampImage && String(block.stampImage).trim()) ||
      (block.name && String(block.name).trim())
  );
}

interface WelderSignatureReportModalProps {

  isOpen: boolean;
  onClose: () => void;
  onGenerateReport: (reportType: string, welder1Signature: any, welder2Signature: any) => void;
  projectName?: string;
  jobNumber?: string;
  weldNumbers?: string[]; // Weld IDs from filtered data
  projectId?: number; // Project ID for API calls
  selectedAssembly?: string; // Selected assembly number
  assemblies?: Array<{ assemblyNumber: string; welds: Array<{ weldNumber: string; weldIdentifier: string }> }>; // Assemblies with weld data
  isCustomerRepresentativeUser?: boolean;
  isQCInspectorUser?: boolean;
  isWelderUser?: boolean;
  currentUserTeamMemberId?: string | number;
  canSign?: boolean;
  canEdit?: boolean;
  selectedPipe?: any; // Selected pipe
  selectedAssemblyData?: any; // Selected assembly data
  isAdminOrSuperAdmin?: boolean;
  onRefresh?: () => void; // Callback to refresh assembly data after report generation
}

export const WelderSignatureReportModal: React.FC<WelderSignatureReportModalProps> = ({
  isOpen,
  onClose,
  onGenerateReport,
  projectName,
  jobNumber,
  weldNumbers = [],
  projectId,
  selectedAssembly,
  assemblies = [],
  isCustomerRepresentativeUser = false,
  isQCInspectorUser = false,
  isWelderUser = false,
  currentUserTeamMemberId,
  canSign = true,
  canEdit = true,
  selectedPipe,
  selectedAssemblyData,
  isAdminOrSuperAdmin = true,
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const { teamMembers, loadTeamMembers } = useTeamMembers();

  // Get report assembly generation state from Redux
  const {
    generateReportAssemblyLoading,
    generateReportAssemblyError,
    generateReportAssemblySuccess,
    generateReportAssemblyData,
    productionSheetSaveSignatureSuccess,
    productionSheetSaveSignatureLoading
  } = useSelector((state: any) => state.productionData);
  const [reportType, setReportType] = useState<string>('zone-view');
  const [reportVersion, setReportVersion] = useState<string>('v2');
  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [storedDownloadData, setStoredDownloadData] = useState<{ url: string, reportType: string } | null>(null);


  const productionData = useSelector((state: any) => state.productionData?.productionData);
  const productionDataError = useSelector((state: any) => state.productionData?.productionDataError);
  const productionDataSuccess = useSelector((state: any) => state.productionData?.productionDataSuccess);
  // Team member lists
  const [qcList, setQcList] = useState<Array<{ value: string, label: string, name?: string, userId?: string }>>([]);
  const [customerRepresentativeList, setCustomerRepresentativeList] = useState<Array<{ value: string, label: string, name?: string, userId?: string }>>([]);
  const [selectedCustomerRepresentativeId, setSelectedCustomerRepresentativeId] = useState<string | null>(null);
  const [selectedQcInspectorId, setSelectedQcInspectorId] = useState<string | null>(null);
  const [signatureData, setSignatureData] = useState<any>(null);
  const [isProductionSheetEditMode, setIsProductionSheetEditMode] = useState(true);
  const [selectedAssemblyProductionData, setSelectedAssemblyProductionData] = useState<any>(null);
  const [isWeldReportExpanded, setIsWeldReportExpanded] = useState<boolean>(false);
  const [isEdited, setIsEdited] = useState<boolean>(true);
  const [reportGenerated, setReportGenerated] = useState<boolean>(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const generatedBy = user && user['custom:first_name'] && user['custom:last_name'] ? user['custom:first_name'] + " " + user['custom:last_name'] :
  (user && user['custom:first_name'] ? user['custom:first_name'] : '') || '';
  // Signature handlers — changing inspector clears that signature (same pattern as MaterialReportModal)
  const handleQcSignatureChange = (value: string | null) => {
    if (selectedQcInspectorId !== value) {
      setSignatureData((prev: any) => ({
        signature: {
          ...prev?.signature,
          qc: {
            name: '',
            signature: '',
            stampImage: '',
            timestamp: '',
            comment: ''
          }
        }
      }));
    }
    setIsEdited(false);
    setSelectedQcInspectorId(value);
  };

  const handleCustomerSignatureChange = (value: string | null) => {
    if (selectedCustomerRepresentativeId !== value) {
      setSignatureData((prev: any) => ({
        signature: {
          ...prev?.signature,
          customer: {
            name: '',
            signature: '',
            stampImage: '',
            timestamp: '',
            comment: ''
          }
        }
      }));
    }
    setIsEdited(false);
    setSelectedCustomerRepresentativeId(value);
  };



  const OnHandleSIngaure = (data: { signature: string; name: string; comment?: string; stampImage?: string; type: 'customer' | 'qc' }) => {
    setSignatureData((prev: any) => ({
      ...prev,
      signature: {
        ...prev?.signature,
        [data.type]: {
          signature: data.signature,
          name: data.name,
          comment: data.comment,
          stampImage: data.stampImage,
          timestamp: new Date().toISOString()
        }
      }
    }));
    setIsEdited(false);
  };

  // Helper function to extract download URL from various response structures
  const getDownloadUrl = (response: any) => {
    if (!response) return null;

    if (response.downloadUrl) {
      return response.downloadUrl;
    }

    // Handle structure: { data: { data: { downloadUrl: "..." } } }
    if (response.data?.data?.downloadUrl) {
      return response.data.data.downloadUrl;
    }

    // Handle structure: { data: { downloadUrl: "..." } }
    if (response.data?.downloadUrl) {
      return response.data.downloadUrl;
    }

    if (response.nestedData?.data?.downloadUrl) {
      return response.nestedData.data.downloadUrl;
    }
    if (response.nestedData?.downloadUrl) {
      return response.nestedData.downloadUrl;
    }

    if (response.data?.data?.data?.data?.downloadUrl) {
      return response.data.data.data.data.downloadUrl;
    }

    if (response.data?.data?.data?.downloadUrl) {
      return response.data.data.data.downloadUrl;
    }

    return null;
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const [isColumnConfigModalOpen, setIsColumnConfigModalOpen] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(['current', 'volts', 'travel_speed', 'wire_speed', 'heat']);
  const [projectColumns, setProjectColumns] = useState<string[]>([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState<boolean>(false);
  const [isLoadingProductionData, setIsLoadingProductionData] = useState<boolean>(false);

  const {
    reportGenerationLoading,
    reportGenerationSuccess,
    reportGenerationError,
    reportGenerationData,
    generateReport,
    clearReportStatus
  } = useReportGeneration();

  const loadProductionData = async () => {
    // Use actual project ID from props - prefer selectedAssemblyProductionData if available
    const dataSource = selectedAssemblyProductionData || selectedAssemblyData;
    const projectId = dataSource?.projectId || selectedPipe?.projectId;
    const pipeId = dataSource?.pipeId || selectedPipe?.pipeId || selectedPipe?.id;
    // Get productionId from dataSource
    const productionId = dataSource?.productionId;

    if (projectId && pipeId && productionId) {
      try {
        setIsLoadingProductionData(true);
        await dispatch(fetchProductionData({
          projectId: projectId,
          pipe_id: pipeId,
          production_id: productionId
        }));
      } catch (error: any) {
        Notification.error('Failed to load production data', error?.message || 'An error occurred');
      } finally {

      }
    } else {
      console.warn('Missing required IDs for loadProductionData:', { projectId, pipeId, productionId });
    }
  };

  // Update selectedAssemblyProductionData when productionData is fetched
  useEffect(() => {
    if (productionData && productionData.length > 0) {
      setIsLoadingProductionData(false);
      const fetchedData = productionData[0];

      setSelectedAssemblyProductionData(fetchedData);
    } else if (!productionData && selectedAssemblyData) {
      // Fallback to prop if API data is not available
      // console.log('Using fallback selectedAssemblyData:', {
      //   productionId: selectedAssemblyData?.productionId,
      //   hasSignature: !!selectedAssemblyData?.signature
      // });
      setIsLoadingProductionData(false);
      setSelectedAssemblyProductionData(selectedAssemblyData);
    }
  }, [productionData]);

  // Function to get consistent description for all report types
  const getReportDescription = (reportType: string) => {
    const baseDescription = reportType === 't-logs' ? 'Generate T-Logs report' :
      reportType === 'pass-view' ? 'Generate Pass View report' :
        'Generate Zone View report';

    if (weldNumbers.length > 0) {
      return `${baseDescription} for selected welds`;
    }

    return `${baseDescription} for selected assembly`;
  };

  const reportTypes = useMemo(() => [

    // { value: 'pass-view', label: 'Pass View Report', icon: Mail, description: getReportDescription('pass-view') },
    { value: 'zone-view', label: 'Zone View Report', icon: MapPin, description: getReportDescription('zone-view') }
  ], [weldNumbers]);

  // Clear report status when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      clearReportStatus();
      setDownloadUrl('');
      setStoredDownloadData(null);

      // Fetch production data when modal opens
      loadProductionData();

      if (projectId) {
        loadProjectColumns();
        if (!teamMembers || teamMembers.length === 0) {
          loadTeamMembers(projectId);
        }
        else {
          loadtetAllTeamMembers();
        }
      }
    } else {
      // Clear signature data when modal closes
      setSignatureData(null);
      setSelectedCustomerRepresentativeId(null);
      setSelectedQcInspectorId(null);
      setSelectedAssemblyProductionData(null);
      setIsWeldReportExpanded(false);
      // Clear production data from Redux to prevent showing previous data
      dispatch(clearProductionData());
    }
  }, [isOpen, clearReportStatus, projectId, loadTeamMembers, dispatch]);

  // Load team members when they change
  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      //  console.log('selectedAssemblyData', selectedAssemblyData);
      loadtetAllTeamMembers();
    }
  }, [teamMembers]);

  const signatureListDataSource = selectedAssemblyProductionData || selectedAssemblyData;

  /** Include saved signer ids not present in the filtered team list so Select + other users' signatures display correctly. */
  const customerRepresentativeListForSelect = useMemo(() => {
    const base = [...customerRepresentativeList];
    const saved = signatureListDataSource?.signature?.customer;
    if (saved?.customerRepresentativeId != null && saved.customerRepresentativeId !== '') {
      const v = String(saved.customerRepresentativeId);
      if (!base.some((m) => m.value === v)) {
        const label = saved.name
          ? `${saved.name} (signed)`
          : `Customer representative (${v})`;
        base.push({
          value: v,
          label,
          name: saved.name || undefined,
          userId: undefined,
        });
      }
    }
    return base;
  }, [customerRepresentativeList, signatureListDataSource]);

  const qcListForSelect = useMemo(() => {
    const base = [...qcList];
    const saved = signatureListDataSource?.signature?.qc;
    if (saved?.qcInspectorId != null && saved.qcInspectorId !== '') {
      const v = String(saved.qcInspectorId);
      if (!base.some((m) => m.value === v)) {
        const label = saved.name ? `${saved.name} (signed)` : `QC inspector (${v})`;
        base.push({
          value: v,
          label,
          name: saved.name || undefined,
          userId: undefined,
        });
      }
    }
    return base;
  }, [qcList, signatureListDataSource]);

  // Restore saved signatures + dropdown selection (id match, then name fallback; always keep signature payload when present).
  useEffect(() => {
    const dataSource = selectedAssemblyProductionData || selectedAssemblyData;
    if (!isOpen || !dataSource) return;

    const savedSignature = dataSource.signature;
    if (!savedSignature) return;

    const restoredSignatureData: any = { signature: {} };

    if (savedSignature.qc) {
      const q = savedSignature.qc;
      const qcPayload = {
        name: q.name || '',
        signature: q.signature || '',
        timestamp: q.timestamp || '',
        stampImage: q.stampImage || '',
        comment: q.comment || '',
      };
      const savedQcId = q.qcInspectorId;
      const qcHasPayload = hasMeaningfulSavedSignatureBlock(q);

      if (savedQcId != null && savedQcId !== '') {
        const resolvedQc = resolveSavedIdToTeamMemberSelectValueWithNameFallback(
          savedQcId,
          q.name,
          qcList
        );
        if (resolvedQc) {
          setSelectedQcInspectorId(resolvedQc);
        } else if (qcHasPayload) {
          setSelectedQcInspectorId(String(savedQcId));
        } else {
          setSelectedQcInspectorId(null);
        }
      } else {
        setSelectedQcInspectorId(null);
      }

      if (qcHasPayload) {
        restoredSignatureData.signature.qc = qcPayload;
      }
    } else {
      setSelectedQcInspectorId(null);
    }

    if (savedSignature.customer) {
      const c = savedSignature.customer;
      const customerPayload = {
        name: c.name || '',
        signature: c.signature || '',
        timestamp: c.timestamp || '',
        stampImage: c.stampImage || '',
        comment: c.comment || '',
      };
      const savedCrId = c.customerRepresentativeId;
      const customerHasPayload = hasMeaningfulSavedSignatureBlock(c);

      if (savedCrId != null && savedCrId !== '') {
        const resolvedCr = resolveSavedIdToTeamMemberSelectValueWithNameFallback(
          savedCrId,
          c.name,
          customerRepresentativeList
        );
        if (resolvedCr) {
          setSelectedCustomerRepresentativeId(resolvedCr);
        } else if (customerHasPayload) {
          setSelectedCustomerRepresentativeId(String(savedCrId));
        } else {
          setSelectedCustomerRepresentativeId(null);
        }
      } else {
        setSelectedCustomerRepresentativeId(null);
      }

      if (customerHasPayload) {
        restoredSignatureData.signature.customer = customerPayload;
      }
    } else {
      setSelectedCustomerRepresentativeId(null);
    }

    const keys = Object.keys(restoredSignatureData.signature);
    if (keys.length > 0) {
      setSignatureData(restoredSignatureData);
      setIsEdited(true);
    } else {
      setSignatureData(null);
    }
  }, [
    isOpen,
    selectedAssemblyProductionData,
    selectedAssemblyData,
    qcList,
    customerRepresentativeList,
  ]);

  // Show/hide download button based on report type match
  useEffect(() => {
    if (storedDownloadData) {
      if (storedDownloadData.reportType === reportType) {
        setDownloadUrl(storedDownloadData.url);
      } else {
        setDownloadUrl('');
      }
    }
  }, [reportType, storedDownloadData]);


  //Collect all documents from the assembly
  const weldReportDocuments = useMemo(() => {
    const documents: any[] = [];

    if (!selectedAssemblyProductionData) return documents;

    const productionSheet = selectedAssemblyProductionData.productionSheet;

    if (productionSheet?.weldReportDocs) {
      const weldReportDocs = productionSheet.weldReportDocs;

      // Sort by versionNumber descending (latest first)
      const sortedWeldReportDocs = [...weldReportDocs].sort((a, b) => {
        const versionA = a.details?.versionNumber || 0;
        const versionB = b.details?.versionNumber || 0;
        return versionB - versionA;
      });

      sortedWeldReportDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `weld-report-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Weld Report Document',
            type: 'WELD REPORT',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Weld Report',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: doc.details?.versionNumber || 0,
            isLatest: index === 0
          });
        }
      });
    }
    return documents;
  }, [selectedAssemblyProductionData]);
  // Function to load project's column configuration
  const loadProjectColumns = async () => {
    if (!projectId) return;

    setIsLoadingColumns(true);
    try {
      const response = await getReportColumns(projectId);
      const reportColumns = response.data.project.report_columns;

      const enabledColumns = Object.entries(reportColumns)
        .filter(([key, value]) => key !== 'is_high_low_avg_required' && value === true)
        .map(([key]) => key);

      setProjectColumns(enabledColumns);
    } catch (error) {
      setProjectColumns(['current', 'volts', 'travel_speed', 'wire_speed', 'heat']);
    } finally {
      setIsLoadingColumns(false);
    }
  };

  // Watch for successful report generation
  useEffect(() => {
    if (reportGenerationSuccess && reportGenerationData && isOpen) {
      if (reportGenerationData.isV2 && getDownloadUrl(reportGenerationData)) {
        const downloadUrl = getDownloadUrl(reportGenerationData);
        setDownloadUrl(downloadUrl);
        setStoredDownloadData({
          url: downloadUrl,
          reportType: reportGenerationData.reportType
        });

        Notification.success('Success', `Report generated successfully! Click the download button to get your report.`);
      } else {
        Notification.success('Success', 'Report generation initiated successfully!');
      }
    }
  }, [reportGenerationSuccess, reportGenerationData, isOpen]);

  // // Watch for report generation errors
  // useEffect(() => {
  //   if (reportGenerationError &&  ) {
  //     Notification.error('Report Generation Failed', reportGenerationError);
  //   }
  // }, [reportGenerationError]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Check if signatures have changed compared to saved data
  const hasSignatureChanges = useMemo(() => {
    // Use fetched production data if available, otherwise fallback to selectedAssemblyData
    const dataSource = selectedAssemblyProductionData || selectedAssemblyData;

    if (!dataSource?.signature) {
      // No saved signatures, check if current signatures exist
      return signatureData && signatureData.signature &&
        (signatureData.signature.qc || signatureData.signature.customer);
    }

    const savedSignature = dataSource.signature;
    const currentSignature = signatureData?.signature;

    if (!currentSignature) {
      // Current has no signatures but saved has some - changed
      return savedSignature.qc || savedSignature.customer;
    }

    // Compare QC signature
    const qcChanged =
      (savedSignature.qc && !currentSignature.qc) || // Removed QC
      (!savedSignature.qc && currentSignature.qc) || // Added QC
      (savedSignature.qc && currentSignature.qc && (
        savedSignature.qc.signature !== currentSignature.qc.signature ||
        savedSignature.qc.name !== currentSignature.qc.name ||
        savedSignature.qc.stampImage !== currentSignature.qc.stampImage ||
        savedSignature.qc.comment !== currentSignature.qc.comment
      ));

    // Compare Customer signature
    const customerChanged =
      (savedSignature.customer && !currentSignature.customer) || // Removed Customer
      (!savedSignature.customer && currentSignature.customer) || // Added Customer
      (savedSignature.customer && currentSignature.customer && (
        savedSignature.customer.signature !== currentSignature.customer.signature ||
        savedSignature.customer.name !== currentSignature.customer.name ||
        savedSignature.customer.stampImage !== currentSignature.customer.stampImage ||
        savedSignature.customer.comment !== currentSignature.customer.comment
      ));

    return qcChanged || customerChanged;
  }, [selectedAssemblyProductionData, selectedAssemblyData, signatureData]);

  // Check if both signatures are already saved and unchanged
  const isBothSignaturesSaved = useMemo(() => {
    // Use fetched production data if available, otherwise fallback to selectedAssemblyData
    const dataSource = selectedAssemblyProductionData || selectedAssemblyData;

    if (!dataSource?.signature) return false;

    const savedSignature = dataSource.signature;
    const currentSignature = signatureData?.signature;

    // Both must exist in saved data
    if (!savedSignature.qc || !savedSignature.customer) return false;

    // Both must exist in current data
    if (!currentSignature?.qc || !currentSignature?.customer) return false;

    // Check if they match exactly (no changes)
    const qcMatches =
      savedSignature.qc.signature === currentSignature.qc.signature &&
      savedSignature.qc.name === currentSignature.qc.name &&
      savedSignature.qc.stampImage === currentSignature.qc.stampImage &&
      savedSignature.qc.comment === currentSignature.qc.comment;

    const customerMatches =
      savedSignature.customer.signature === currentSignature.customer.signature &&
      savedSignature.customer.name === currentSignature.customer.name &&
      savedSignature.customer.stampImage === currentSignature.customer.stampImage &&
      savedSignature.customer.comment === currentSignature.customer.comment;

    // Both signatures exist and match exactly - no need to save
    return qcMatches && customerMatches;
  }, [selectedAssemblyProductionData, selectedAssemblyData, signatureData]);

  const getGenerateReportTooltip = () => {
    if (!signatureData?.signature?.qc || !signatureData?.signature?.customer) {
      return 'Both QC and Customer signatures are required';
    }
    if (!isEdited) {
      return 'Save your signatures before generating the report';
    }
    return '';
  };

  const saveSignature = () => {
    // Use fetched production data if available, otherwise fallback to selectedAssemblyData
    const dataSource = selectedAssemblyProductionData || selectedAssemblyData;

    // Validate required fields
    if (!dataSource?.productionId) {
      Notification.error('Production ID Required', 'Production ID is required to save the signature.');
      return;
    }

    // Validate signatures are provided
    if (!signatureData || !signatureData.signature) {
      Notification.error('Signature Required', 'Please add at least one signature before saving.');
      return;
    }

    // Prepare signature payload
    const signaturePayload: any = {};

    if (signatureData.signature?.qc) {
      signaturePayload.qc = {
        qcInspectorId: selectedQcInspectorId,
        name: signatureData.signature.qc.name || '',
        timestamp: signatureData.signature.qc.timestamp || new Date().toISOString(),
        signature: signatureData.signature.qc.signature || '',
        stampImage: signatureData.signature.qc.stampImage || '',
        comment: signatureData.signature.qc.comment || ''
      };
    }

    if (signatureData.signature?.customer) {
      signaturePayload.customer = {
        customerRepresentativeId: selectedCustomerRepresentativeId,
        name: signatureData.signature.customer.name || '',
        signature: signatureData.signature.customer.signature || '',
        timestamp: signatureData.signature.customer.timestamp || new Date().toISOString(),
        stampImage: signatureData.signature.customer.stampImage || '',
        comment: signatureData.signature.customer.comment || ''
      };
    }

    // Prepare API payload
    const payload = {
      productionId: dataSource?.productionId,
      signature: signaturePayload
    };

    // Dispatch the action
    dispatch(productionSheetSaveSignature(payload));
  };

  const handleGenerateReport = () => {
    // Validate required fields
    if (!projectId) {
      Notification.error('Project ID Required', 'Project ID is required to generate the report.');
      return;
    }

    if (!selectedPipe?.pipeId) {
      Notification.error('Pipe ID Required', 'Pipe ID is required to generate the report.');
      return;
    }

    // Use fetched production data if available, otherwise fallback to selectedAssemblyData
    const dataSource = selectedAssemblyProductionData || selectedAssemblyData;
    const productionId = dataSource?.productionId || dataSource?.productionSheet?.productionSheetId;

    if (!productionId) {
      Notification.error('Production ID Required', 'Production ID is required to generate the report.');
      return;
    }
    // console.log('weldNumbers', weldNumbers);
    // console.log('selectedAssemblyData', selectedAssemblyData);
    // console.log('assemblies', assemblies);

    // Validate that we have either selected weldNumbers or assemblies
    if ((!weldNumbers || weldNumbers.length === 0) && (!assemblies || assemblies.length === 0)) {
      Notification.error('Weld Numbers Required', 'At least one weld number is required to generate the report.');
      return;
    }

    // Validate signatures are provided
    if (!signatureData || !signatureData.signature?.qc || !signatureData.signature?.customer) {
      Notification.error('Signatures Required', 'Please add both signatures (QC and Customer Representative) before generating the report.');
      return;
    }

    // Prepare signatures object
    const signatures: any = {};

    if (signatureData.signature?.qc) {
      signatures.qc = {
        name: signatureData.signature.qc.name || '',
        timestamp: signatureData.signature.qc.timestamp || new Date().toISOString(),
        signature: signatureData.signature.qc.signature || '',
        stampImage: signatureData.signature.qc.stampImage || ''

      };
    }

    if (signatureData.signature?.customer) {
      signatures.customer = {
        name: signatureData.signature.customer.name || '',
        signature: signatureData.signature.customer.signature || '',
        timestamp: signatureData.signature.customer.timestamp || new Date().toISOString(),
        stampImage: signatureData.signature.customer.stampImage || ''
      };
    }

    // Determine weld numbers: use selected weldNumbers if available, otherwise use all from assemblies
    let finalWeldNumbers: string[] = [];

    if (weldNumbers && weldNumbers.length > 0) {
      // Use selected weld numbers from props
      finalWeldNumbers = weldNumbers;
    } else if (assemblies && assemblies.length > 0) {
      // Extract all weld identifiers from assemblies
      // Handle both flat structure (direct array of welds) and nested structure (assemblies with welds array)
      finalWeldNumbers = assemblies.flatMap((assembly: any) => {
        // If assembly has welds array, extract from there
        if (assembly.welds && Array.isArray(assembly.welds)) {
          return assembly.welds.map((weld: any) => weld.weldIdentifier || weld.weldNumber);
        }
        // Otherwise, treat as flat structure - return single identifier
        const identifier = assembly.weldIdentifier || assembly.weldNumber;
        return identifier ? [identifier] : [];
      }).filter((id: string) => id); // Filter out any undefined/null values
    }

    if (finalWeldNumbers.length === 0) {
      Notification.error('Weld Numbers Required', 'At least one weld number is required to generate the report.');
      return;
    }

    // Prepare payload
    const payload = {
      projectId,
      pipeId: selectedPipe?.pipeId,
      reportType: reportType,
      reportVersion: reportVersion,
      productionId: productionId,
      weldNumbers: finalWeldNumbers,
      showEnbridgeLogo: true,
      generatedBy: generatedBy,
      signatures
    };

    // Dispatch the action
    dispatch(generateReportAssembly(payload));
    // Notification.success('Report Generation Initiated', 'Weld report generation initiated successfully!');
  };

  // // Auto-generate report after successful signature save if both signatures are valid
  // useEffect(() => {
  //   if (productionSheetSaveSignatureSuccess && !productionSheetSaveSignatureLoading) {
  //     // Check if both signatures are valid
  //     if (signatureData && signatureData.signature?.qc && signatureData.signature?.customer) {
  //       // Validate all required fields before generating report
  //       if (projectId && selectedPipe?.pipeId && selectedAssemblyData?.productionSheet?.productionSheetId && assemblies && assemblies.length > 0) {
  //         // Both signatures are valid and all required fields are present, automatically generate report
  //         handleGenerateReport();
  //         // Clear the signature save success state to prevent infinite loops
  //         dispatch(clearProductionSheetSaveSignature());
  //       }
  //     }
  //   }
  // }, [productionSheetSaveSignatureSuccess, productionSheetSaveSignatureLoading, signatureData, projectId, selectedPipe, selectedAssemblyData, assemblies, reportType, reportVersion, dispatch]);

  // Handle signature save success - refresh production data and clear success state
  useEffect(() => {
    if (productionSheetSaveSignatureSuccess && !productionSheetSaveSignatureLoading) {
      // Refresh production data to get updated signatures
      loadProductionData();

      // Mark as saved (not edited) after successful save
      setIsEdited(true);

      // Clear the success state after a delay to allow user to see the success
      const timer = setTimeout(() => {
        dispatch(clearProductionSheetSaveSignature());
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [productionSheetSaveSignatureSuccess, productionSheetSaveSignatureLoading, dispatch]);

  // Handle success/error from Redux
  useEffect(() => {
    if (generateReportAssemblySuccess && generateReportAssemblyData) {
    //  console.log('generateReportAssemblySuccess', generateReportAssemblySuccess);
      setReportGenerated(true);
      setTimeout(() => {
      //  console.log('generateReportAssemblySuccess timeout');
        loadProductionData();
        setIsLoadingProductionData(false);
        setReportGenerated(false);
        if (onRefresh) {
          onRefresh();
        }
      }, 5000);
      // Extract download URL from response
      const downloadUrlFromResponse = getDownloadUrl(generateReportAssemblyData);

      if (downloadUrlFromResponse) {
        // Set download URL and store with report type
        setDownloadUrl(downloadUrlFromResponse);
        setStoredDownloadData({
          url: downloadUrlFromResponse,
          reportType: reportType
        });

        //Notification.success('Report Generated', 'Weld report generated successfully! Click the download button to get your report.');
      } else {
        Notification.success('Report Generated', 'Weld report generated successfully!');
      }

      // Refresh assembly data after successful report generation

      // setTimeout(() => {

      // }, 2000);


      // Clear Redux state after handling success
      dispatch(clearGenerateReportAssembly());

    }
  }, [generateReportAssemblySuccess, generateReportAssemblyData, onRefresh, dispatch, reportType]);

  // useEffect(() => {
  //   if (generateReportAssemblyError) {
  //     Notification.error('Report Generation Failed', generateReportAssemblyError);
  //   }
  // }, [generateReportAssemblyError]);

  const handleColumnConfigSave = (columns: string[]) => {
    setSelectedColumns(columns);
    setProjectColumns(columns);
  };

  const loadtetAllTeamMembers = async () => {
    if (teamMembers) {

      const qcInspectors = teamMembers
        .filter((member: any) => member?.team_member_role === 'Quality Control' || member?.team_member_role === 'QC' || member?.team_member_role === 'QA/QC')
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`,
          userId: member?.user_id
        }));



      const customerReps = teamMembers
        .filter(
          (member: any) =>
            member?.team_member_role === 'Customer Representative' ||
            member?.team_member_role === 'Client Representative'
        )
        .map((member: any) => ({
          value: member.team_member_id.toString(),
          name: `${member.user.first_name} ${member.user.last_name}`,
          label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`,
          userId: member?.user_id
        }));
      // const managers = teamMembers
      //   .filter((member: any) => member.projectLevelRole?.project_role_name === 'Manager' || member.projectLevelRole?.project_role_name === 'Project Manager')
      //   .map((member: any) => ({
      //     value: member.team_member_id.toString(),
      //     label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`
      //   }));

      // const supervisors = teamMembers
      //   .filter((member: any) => member.projectLevelRole?.project_role_name === 'Supervisor' || member.projectLevelRole?.project_role_name === 'Project Supervisor')
      //   .map((member: any) => ({
      //     value: member.team_member_id.toString(),
      //     label: `${member.user.first_name} ${member.user.last_name} (${member.user.email})`
      //   }));



      //console.log('QC Inspectors:', qcInspectors);
      // console.log('Customer Reps:', customerReps);

      // Update state with filtered lists
      //setQcList(qcInspectors);
      //setCustomerRepresentativeList(customerReps);

      if (isAdminOrSuperAdmin) {
        setQcList(qcInspectors);
      }
      else if (isQCInspectorUser && qcInspectors.length > 0) {
        setQcList(
          qcInspectors.filter(
            (member: any) =>
              currentUserTeamMemberId != null &&
              String(member.userId) === String(currentUserTeamMemberId)
          )
        );
      }
      else {
        setQcList([]);
      }

      if (isAdminOrSuperAdmin) {
        setCustomerRepresentativeList(customerReps);
      }
      else if (isCustomerRepresentativeUser && customerReps.length > 0) {
        setCustomerRepresentativeList(
          customerReps.filter(
            (member: any) =>
              currentUserTeamMemberId != null &&
              String(member.userId) === String(currentUserTeamMemberId)
          )
        );
      }
      else {
        setCustomerRepresentativeList([]);
      }


    } else {
      console.log('No team members available');
    }
  }
  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        // Clear production data when modal closes (via back button or clicking outside)
        setSelectedAssemblyProductionData(null);
        dispatch(clearProductionData());
      }
      onClose();
    }}>
      <DialogContent
        className={cn(
          "w-[95vw] max-w-[1400px] min-w-[320px] p-0 gap-0 border bg-white shadow-2xl overflow-hidden flex flex-col max-h-[90vh]",
          "sm:w-[90vw] sm:max-w-[800px]",
          "md:w-[85vw] md:max-w-[1000px]",
          "lg:w-[80vw] lg:max-w-[1200px]",
          "xl:w-[75vw] xl:max-w-[1400px]"
        )}
      >
        <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => {
                // Clear production data before closing
                setSelectedAssemblyProductionData(null);
                dispatch(clearProductionData());
                onClose();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              variant="outline"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </Button>
            <div>
              <DialogTitle className="text-lg font-bold text-gray-900">
                Weld  Report Generation
                {reportGenerationLoading && (
                  <span className="ml-2 text-sm font-normal text-red-600">
                    (Generating...)
                  </span>
                )}
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-1">
                {projectName && jobNumber ? `Project: ${projectName} | Job: ${jobNumber}` :
                  projectName ? `Project: ${projectName}` :
                    jobNumber ? `Job: ${jobNumber}` : ''}
                {selectedAssembly && (
                  <span className="ml-2">
                    | Assembly: {selectedAssembly}
                  </span>
                )}
                {weldNumbers.length > 0 && (
                  <span className="ml-2">
                    | {weldNumbers.length} Welds Selected
                  </span>
                )}
              </p>
            </div>
          </div>
        </DialogHeader>

        {assemblies && assemblies.length === 0 ? (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-700 font-medium text-lg">No welds selected</p>
                <p className="text-gray-500 text-sm mt-2">Please select welds to generate a report.</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {isLoadingProductionData ? (
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                <div className="space-y-8">
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 mr-3 animate-spin text-gray-600" />
                    <span className="text-gray-700 font-medium">Loading signatures and report data...</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                <div className="space-y-8">
                  {/* Report Type Selection */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-gray-700">Report Type</Label>
                      <Button
                        onClick={() => setIsColumnConfigModalOpen(true)}
                        size="sm"
                        variant="outline"
                        className="h-8 px-3 text-xs"
                        disabled={reportGenerationLoading}
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Configure Columns
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-4">
                      {reportTypes.map((type) => {
                        const IconComponent = type.icon;

                        return (
                          <label
                            key={type.value}
                            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${reportType === type.value
                              ? ' bg-red-50'
                              : 'border-gray-200 hover:border-gray-300'
                              }`}
                          >
                            <input
                              type="radio"
                              name="reportType"
                              value={type.value}
                              checked={reportType === type.value}
                              onChange={(e) => setReportType(e.target.value)}
                              disabled={reportGenerationLoading}
                              className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                            />
                            <IconComponent className="h-5 w-5 text-gray-600" />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-gray-900">{type.label}</span>
                              <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {/* Version Selection - Only show for Pass View and Zone View */}
                    {/* {(reportType === 'pass-view' || reportType === 'zone-view') && (
                  <div className="space-y-3 mt-4">
                    <Label className="text-sm font-medium text-gray-700">Report Version</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${reportVersion === 'v1'
                            ? ' bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <input
                          type="radio"
                          name="reportVersion"
                          value="v1"
                          checked={reportVersion === 'v1'}
                          onChange={(e) => setReportVersion(e.target.value)}
                          disabled={reportGenerationLoading}
                          className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Version 1 (Email)</span>
                          <p className="text-xs text-gray-500 mt-1">Report sent directly to email after generation</p>
                        </div>
                      </label>
                      <label
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${reportVersion === 'v2'
                            ? ' bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <input
                          type="radio"
                          name="reportVersion"
                          value="v2"
                          checked={reportVersion === 'v2'}
                          onChange={(e) => setReportVersion(e.target.value)}
                          disabled={reportGenerationLoading}
                          className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Version 2 (Download)</span>
                          <p className="text-xs text-gray-500 mt-1">Get download link to access report immediately</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )} */}
                  </div>

              {teamMembers && teamMembers.length > 0 && (
                <Signature
                    customerSignature={selectedCustomerRepresentativeId}
                    qcSignature={selectedQcInspectorId}
                    customerRepresentativeList={customerRepresentativeListForSelect}
                    qcList={qcListForSelect}
                    handleQcSignatureChange={handleQcSignatureChange}
                    handleCustomerSignatureChange={handleCustomerSignatureChange}
                    signatureData={signatureData}
                    onSignatureChange={OnHandleSIngaure}
                    canSign={canSign}
                    canEdit={canEdit}
                    isEditMode={isProductionSheetEditMode}
                    isCustomerRepresentativeUser={isCustomerRepresentativeUser}
                    isQCInspectorUser={isQCInspectorUser}
                    isWelderUser={isWelderUser}
                    currentUserTeamMemberId={currentUserTeamMemberId}
                    isAdminOrSuperAdmin={isAdminOrSuperAdmin}
                  />
                )}
                  {/* Status Messages */}
                  {reportGenerationSuccess && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700">Report generation initiated successfully!</span>
                    </div>
                  )}

                  {/* Weld Report Documents Section - Show latest version with expand for older versions */}
                  {(() => {
                    const latestDoc = weldReportDocuments.find(doc => doc.isLatest) || weldReportDocuments[0];
                    const olderDocs = weldReportDocuments.filter(doc => !doc.isLatest && doc !== latestDoc);
                    const hasMultipleVersions = olderDocs.length > 0;

                    return weldReportDocuments.length > 0 && latestDoc ? (
                      <div className="space-y-4 pt-6 border-t">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide pb-2 border-b">
                          WELD REPORT ({weldReportDocuments.length})
                        </h3>
                        <div className="space-y-2">
                          {/* Latest Version - Show at top */}
                          <div className="space-y-2">
                            <div
                              className={`flex items-center justify-between p-4 rounded-lg transition-colors border ${latestDoc.isLatest
                                ? 'bg-blue-50 hover:bg-blue-100'
                                : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                                }`}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {latestDoc.name}
                                    </p>
                                    {latestDoc.versionNumber && (
                                      <span className="text-xs px-2 py-0.5 text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-full font-medium">
                                        v{latestDoc.versionNumber}
                                      </span>
                                    )}
                                    {latestDoc.isLatest && (
                                      <span className="text-xs px-2 py-0.5 text-galvanizing-green bg-[rgba(31,154,58,0.08)] rounded-full font-medium">
                                        Latest
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                    <span className="capitalize">{formatDate(latestDoc?.uploadDate || '')}</span>
                                    <span className="capitalize">{latestDoc?.uploadedBy || ''}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-3 flex-shrink-0">
                                {/* Expand/Collapse Button for multiple versions */}
                                {hasMultipleVersions && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setIsWeldReportExpanded(!isWeldReportExpanded)}
                                    className="flex items-center gap-1"
                                  >
                                    {isWeldReportExpanded ? (
                                      <>
                                        <ChevronUp className="h-4 w-4" />
                                        Hide Versions
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="h-4 w-4" />
                                        Show Versions ({olderDocs.length})
                                      </>
                                    )}
                                  </Button>
                                )}

                                {/* Download Button Only - No Approve Button */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    if (latestDoc.fileUrl) {
                                      const link = document.createElement('a');
                                      link.href = latestDoc.fileUrl;
                                      link.target = '_blank';
                                      link.rel = 'noopener noreferrer';
                                      link.style.display = 'none';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }
                                  }}
                                  className="flex items-center gap-2"
                                  disabled={!latestDoc.fileUrl}
                                >
                                  <Download className="h-4 w-4" />
                                  Download
                                </Button>
                              </div>
                            </div>

                            {/* Older Versions - Only shown when expanded */}
                            {hasMultipleVersions && isWeldReportExpanded && (
                              <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-4">
                                {olderDocs.map((doc) => (
                                  <div
                                    key={doc.id}
                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                                  >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <p className="text-sm font-medium text-gray-700 truncate">
                                            {doc.name}
                                          </p>
                                          {doc.versionNumber && (
                                            <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full font-medium">
                                              v{doc.versionNumber}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                          <span className="capitalize">{formatDate(doc?.uploadDate || '')}</span>
                                          <span className="capitalize">{doc?.uploadedBy || ''}</span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-3 flex-shrink-0">
                                      {/* Download Button Only - No Approve Button for older versions */}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        title="Click to Download report"
                                        onClick={() => {
                                          if (doc.fileUrl) {
                                            const link = document.createElement('a');
                                            link.href = doc.fileUrl;
                                            link.target = '_blank';
                                            link.rel = 'noopener noreferrer';
                                            link.style.display = 'none';
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                          }
                                        }}
                                        className="flex items-center gap-2"
                                        disabled={!doc.fileUrl}
                                      >
                                        <Download className="h-4 w-4" />
                                        Download
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                    ) : null;
                  })()}



                  {/* Download button for successful report generation */}
                  {downloadUrl && storedDownloadData && storedDownloadData.reportType === reportType && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div className="flex-1">
                        <div className="text-sm text-green-700 font-medium">Report generated successfully!</div>
                        <div className="text-xs text-green-600 mt-1">Click download to get your report</div>
                      </div>
                      <Button
                        onClick={handleDownload}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  )}

                  {/* Information message about required signatures */}
                  {(!signatureData || !signatureData.signature?.qc || !signatureData.signature?.customer) && (
                    <div className="flex items-start gap-2 p-3 bg-blue-50 border rounded-lg">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-700">
                        <div className="font-medium mb-1">Required Signatures to Generate Report:</div>
                        <ul className="list-disc list-inside space-y-1">
                          {!signatureData?.signature?.qc && (
                            <li>QC Inspector signature is required</li>
                          )}
                          {!signatureData?.signature?.customer && (
                            <li>Customer Representative signature is required</li>
                          )}
                        </ul>
                        <div className="mt-2 text-xs text-blue-600">
                          Please add the missing signatures and save them before generating the report.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error message for report generation errors */}
                  {reportGenerationError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border rounded-lg">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <div className="text-sm text-red-700">
                        <div className="font-medium">Error: {reportGenerationError}</div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            )}
          </>
        )}

        {!isLoadingProductionData && <DialogFooter className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4 bg-white border-t">
          <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 w-full">
            <Button
              onClick={() => {
                // Clear production data before closing
                setSelectedAssemblyProductionData(null);
                dispatch(clearProductionData());
                onClose();
              }}
              variant="outline"
              className="px-6"
            >
              Cancel
            </Button>

            <Button
              onClick={() => {
                saveSignature();
              }}
              className="px-6"
              disabled={isEdited || productionSheetSaveSignatureLoading || productionSheetSaveSignatureSuccess || isBothSignaturesSaved}
            >
              {productionSheetSaveSignatureLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : productionSheetSaveSignatureSuccess ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Saved
                </>
              ) : (
                'Save Signature'
              )}
            </Button>

            <Button
              onClick={handleGenerateReport}
              title={getGenerateReportTooltip()}
              className="bg-red-600 hover:bg-red-700 text-white px-6"
              disabled={
                generateReportAssemblyLoading ||
                reportGenerationLoading ||
                reportGenerated ||
                !isEdited ||
                !signatureData ||
                !signatureData.signature?.qc ||
                !signatureData.signature?.customer
              }
            >
              {(generateReportAssemblyLoading || reportGenerationLoading || reportGenerated) ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate Report'
              )}
            </Button>
          </div>
        </DialogFooter>
        }
      </DialogContent>

      {/* Signature Dialogs */}
      {/* <SignatureDialog
        isOpen={isWelder1SignatureDialogOpen}
        onClose={() => setIsWelder1SignatureDialogOpen(false)}
        onSave={handleSaveWelder1Signature}
        title="Add Welder 1 Signature"
        initialName={welder1Signature?.name || ''}
        initialSignature={welder1Signature?.signature || null}
        initialComment={welder1Signature?.comment || ''}
      />

      <SignatureDialog
        isOpen={isWelder2SignatureDialogOpen}
        onClose={() => setIsWelder2SignatureDialogOpen(false)}
        onSave={handleSaveWelder2Signature}
        title="Add Welder 2 Signature"
        initialName={welder2Signature?.name || ''}
        initialSignature={welder2Signature?.signature || null}
        initialComment={welder2Signature?.comment || ''}
      /> */}

      {/* Column Configuration Modal */}
      <ReportColumnConfigModal
        isOpen={isColumnConfigModalOpen}
        onClose={() => setIsColumnConfigModalOpen(false)}
        onSave={handleColumnConfigSave}
        initialSelectedColumns={selectedColumns}
        projectId={projectId}
      />
    </Dialog>
  );
};


